import { createAction } from 'Lib/reduxUtils';
import { ValidProfileFields } from 'Services/ProfileService/constants';

import { SelfieVerificationStatus } from 'Containers/ModalManager/modals/SelfieVerification/constants';
import { IdVerificationStatus } from 'Containers/ModalManager/modals/SelfieVerificationV2/types';

import { emptyArray } from '../../lib/constants/placeholder';
import {
  CLEAR_READ_RECEIPT_ERROR,
  CONSUME_READ_RECEIPT,
  CONSUME_READ_RECEIPT_ERROR,
  CONSUME_READ_RECEIPT_SUCCESS,
  DELETE_USER_INTERESTS_SUCCESS,
  DELETE_USER_PHOTO,
  DELETE_USER_PHOTO_FAILURE,
  DELETE_USER_PHOTO_SUCCESS,
  FETCH_PROFILE,
  FETCH_PROFILE_ERROR,
  FETCH_PROFILE_REQUEST,
  FETCH_PROFILE_SUCCESS,
  FETCH_READ_RECEIPT_SUBSCRIPTION,
  FETCH_READ_RECEIPT_SUBSCRIPTION_SUCCESS,
  GET_FACEBOOK_EMAIL,
  GET_FACEBOOK_EMAIL_SUCCESS,
  REFRESH_PROFILE_NUDGE_RECEIVED,
  SEND_GOLD_TUTORIAL_VIEWED,
  SEND_TUTORIAL_VIEWED_SUCCESS,
  SET_ENABLED_STICKER,
  SET_HAS_USER_VIEWED_ALL_IN_SCREENS_BEFORE,
  SET_HEIGHT_MEASURE_TYPE,
  SET_ID_VERIFICATION_STATUS,
  SET_PHOTO_ORDER,
  SET_PHOTO_ORDER_SUCCESS,
  SET_PROFILE_USER,
  SET_READ_RECEIPT_SUBSCRIPTION,
  SET_READ_RECEIPT_SUBSCRIPTION_SUCCESS,
  SET_SELFIE_VERIFICATION_STATUS,
  SET_USER_INTERESTS,
  TOGGLE_GLOBAL_MODE,
  TOGGLE_STICKER_ENABLED,
  UPDATE_CONTACT_CARD,
  UPDATE_CONTACT_CARD_ERROR,
  UPDATE_CONTACT_CARD_SUCCESS,
  UPDATE_GLOBAL_MODE_PREFERENCES,
  UPDATE_TINDER_U_STATUS,
  UPDATE_USER_DATA_LOCALLY,
  UPDATE_USER_JOB,
  UPDATE_USER_SCHOOL,
  UPLOAD_PHOTO_FAILURE,
  UPLOAD_PHOTO_SUCCESS,
  UPLOAD_PHOTOS,
  USER_DATA_SET,
  USER_DATA_SET_FAILURE,
  USER_DATA_SET_SUCCESS,
  USER_DATA_SET_SUCCESS_WITHOUT_REFRESH_RECS,
  USER_SET_PHOTOS,
} from './constants';
import { SupportedTutorial } from './types';
import { MediaFileMeta, Photo } from './user.types';

export const deleteUserPhoto = createAction(DELETE_USER_PHOTO, (photoNum: string) => photoNum);
export const deleteUserPhotoFailure = createAction(
  DELETE_USER_PHOTO_FAILURE,
  (error: any) => error
);
export const deleteUserPhotoSuccess = createAction(
  DELETE_USER_PHOTO_SUCCESS,
  (photo: { succeeded: string[] }) => photo
);

const defaultFetchProfileOption = {
  inputFields: undefined,
  retryCount: 0,
};

export const fetchProfile = createAction(
  FETCH_PROFILE,
  ({
    inputFields,
    retryCount = 0,
  }: { inputFields?: ValidProfileFields[]; retryCount?: number } = defaultFetchProfileOption) => ({
    inputFields,
    retryCount,
  })
);
export const fetchProfileRequest = createAction(FETCH_PROFILE_REQUEST);
export const fetchProfileError = createAction(FETCH_PROFILE_ERROR, (error: any) => error);
export const fetchProfileSuccess = createAction(
  FETCH_PROFILE_SUCCESS,
  // @TODO: Ideally we'd just use the normalized data as-is, there's a lot of migration to do first.
  ({ entities, result }: Record<string, any>, rawResponse: any = {}) => ({
    ...rawResponse,
    // Normalized data
    entities,
    result,
  })
);
export const setUserData = createAction(USER_DATA_SET, (userDataChanges: Record<string, any>) => ({
  user: userDataChanges,
}));

export const updateUserDataLocally = createAction(
  UPDATE_USER_DATA_LOCALLY,
  (userDataChanges: Record<string, any>) => userDataChanges
);

// profile data changes outside the user object
export const setProfileData = createAction(
  USER_DATA_SET,
  (profileDataChanges: Record<string, any>) => profileDataChanges
);
export const setUserDataSuccess = createAction(
  USER_DATA_SET_SUCCESS,
  (userData: Record<string, any>) => userData
);
export const setUserDataSuccessWithoutRefreshRecs = createAction(
  USER_DATA_SET_SUCCESS_WITHOUT_REFRESH_RECS,
  (userData: Record<string, any>) => userData
);

export const setUserDataFailure = createAction(USER_DATA_SET_FAILURE, (error: any) => error);
export const setUserPhotos = createAction(USER_SET_PHOTOS, (photos: Photo[]) => photos);
export const getFacebookEmail = createAction(GET_FACEBOOK_EMAIL);
export const getFacebookEmailSuccess = createAction(
  GET_FACEBOOK_EMAIL_SUCCESS,
  (data: { email: string }) => data
);
export const setSelfieVerificationStatus = createAction(
  SET_SELFIE_VERIFICATION_STATUS,
  (status: SelfieVerificationStatus) => status
);
export const setIdVerificationStatus = createAction(
  SET_ID_VERIFICATION_STATUS,
  (status: IdVerificationStatus) => status
);

export const updateUserJob = createAction(
  UPDATE_USER_JOB,
  (jobTitle: string = '', company: string = '') => ({
    jobs:
      !jobTitle && !company
        ? emptyArray
        : [
            {
              company: {
                displayed: !!company,
                name: company,
              },
              title: {
                displayed: !!jobTitle,
                name: jobTitle,
              },
            },
          ],
  })
);

export const updateUserSchool = createAction(
  UPDATE_USER_SCHOOL,
  (school?: string, id?: string) => ({
    schools: !school
      ? emptyArray
      : [
          {
            displayed: true,
            name: school,
            school_id: id,
          },
        ],
  })
);

export const updateTinderUStatus = createAction(
  UPDATE_TINDER_U_STATUS,
  (data: Record<string, any>) => data
);

export const setReadReceiptSubscription = createAction(
  SET_READ_RECEIPT_SUBSCRIPTION,
  (shouldEnableReadReceipts: boolean) => ({ shouldEnableReadReceipts })
);
export const fetchReadReceiptSubscription = createAction(FETCH_READ_RECEIPT_SUBSCRIPTION);
export const fetchReadReceiptSubscriptionSuccess = createAction(
  FETCH_READ_RECEIPT_SUBSCRIPTION_SUCCESS,
  (data: Record<string, any>) => data
);
export const setReadReceiptSubscriptionSuccess = createAction(
  SET_READ_RECEIPT_SUBSCRIPTION_SUCCESS,
  (data: Record<string, any>) => data
);
export const consumeReadReceipt = createAction(CONSUME_READ_RECEIPT, (matchId: string) => ({
  matchId,
}));
export const consumeReadReceiptFailure = createAction(CONSUME_READ_RECEIPT_ERROR, (error: any) => ({
  error,
}));
export const consumeReadReceiptSuccess = createAction(
  CONSUME_READ_RECEIPT_SUCCESS,
  (matchId: string) => ({
    matchId,
  })
);
export const clearReadReceiptError = createAction(CLEAR_READ_RECEIPT_ERROR);
export const setPhotoOrder = createAction(SET_PHOTO_ORDER, (photos: string[]) => ({ photos }));
export const setPhotoOrderSuccess = createAction(
  SET_PHOTO_ORDER_SUCCESS,
  (photos: Photo[]) => photos
);

export const setUserInterests = createAction(SET_USER_INTERESTS, (data: any) => data);
export const deleteUserInterestsSuccess = createAction(DELETE_USER_INTERESTS_SUCCESS);

export const setUserOnlinePresenceDisabled = createAction(USER_DATA_SET, (isDisabled: boolean) => ({
  user: {
    user_presence_disabled: isDisabled,
  },
}));
export const uploadPhotos = createAction(
  UPLOAD_PHOTOS,
  (media: { blob: Blob }[], isReplacePhoto: boolean, eventPayload: Record<string, any>) => ({
    media,
    isReplacePhoto,
    eventPayload,
  })
);
export const uploadPhotoSuccess = createAction(
  UPLOAD_PHOTO_SUCCESS,
  (assets: { mediaId: string; processedPhotos: MediaFileMeta[]; originalMediaUrl: string }) =>
    assets
);
export const uploadPhotoFailure = createAction(UPLOAD_PHOTO_FAILURE);
export const toggleGlobalMode = createAction(
  TOGGLE_GLOBAL_MODE,
  ({ enable, includeEnglish }: { enable: boolean; includeEnglish?: boolean }) => ({
    enable,
    includeEnglish,
  })
);
export const updateGlobalModePreferences = createAction(
  UPDATE_GLOBAL_MODE_PREFERENCES,
  (languages: Set<string>, add?: boolean, source?: string) => ({
    languages,
    add,
    source: source ?? 'settings',
  })
);

export const toggleStickerEnabled = createAction(TOGGLE_STICKER_ENABLED);

export const setEnabledSticker = createAction(SET_ENABLED_STICKER, (id: string) => id);

export const updateContactCard = createAction(
  UPDATE_CONTACT_CARD,
  (contactType: string, contactId: string, sessionId: string, source: string) => ({
    contactType,
    contactId,
    sessionId,
    source,
  })
);
export const updateContactCardError = createAction(
  UPDATE_CONTACT_CARD_ERROR,
  (error: any) => error
);
export const updateContactCardSuccess = createAction(
  UPDATE_CONTACT_CARD_SUCCESS,
  (data: Record<string, any>) => data
);

export const sendGoldTutorialViewed = createAction(SEND_GOLD_TUTORIAL_VIEWED);
export const sendTutorialViewedSuccess = createAction(
  SEND_TUTORIAL_VIEWED_SUCCESS,
  (tutorial: SupportedTutorial): { tutorial: SupportedTutorial } => ({
    tutorial,
  })
);

export const setProfileUser = createAction(
  SET_PROFILE_USER,
  (user: Record<string, any>, shouldRefresh: boolean = false) => ({
    user,
    shouldRefresh,
  })
);

export const setHeightMeasurementType = createAction(
  SET_HEIGHT_MEASURE_TYPE,
  (heightMeasureType: string) => ({ heightMeasureType })
);

export const setHasUserViewedAllInScreensBefore = createAction(
  SET_HAS_USER_VIEWED_ALL_IN_SCREENS_BEFORE,
  (hasUserViewedAllInScreensBefore: boolean) => ({ hasUserViewedAllInScreensBefore })
);

export const refreshProfileNudgeReceived = createAction(
  REFRESH_PROFILE_NUDGE_RECEIVED,
  ({ reason: { value } }: { reason: { value: string } }) => ({ reason: value })
);
